/**
 * useInventories Hook
 *
 * React Query hooks for inventories.
 * @see US-12.2 - API CRUD
 * @see US-12.3 - Auto-save
 * @see US-12.5 - Validation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  inventoriesService,
  type QueryInventoriesDto,
  type CreateInventoryDto,
  type UpdateInventoryDto,
  type CancelInventoryDto,
  type SaveProductLinesDto,
} from '../services/inventories.service';

/**
 * Query key factory for inventories
 */
export const inventoryKeys = {
  all: ['inventories'] as const,
  lists: () => [...inventoryKeys.all, 'list'] as const,
  list: (params: QueryInventoriesDto) => [...inventoryKeys.lists(), params] as const,
  details: () => [...inventoryKeys.all, 'detail'] as const,
  detail: (id: string) => [...inventoryKeys.details(), id] as const,
  summary: (id: string) => [...inventoryKeys.all, 'summary', id] as const,
  preview: (id: string) => [...inventoryKeys.all, 'preview', id] as const,
  kpis: (startDate?: string, endDate?: string) => [...inventoryKeys.all, 'kpis', startDate, endDate] as const,
};

/**
 * Hook to fetch inventories list with pagination
 */
export function useInventories(params: QueryInventoriesDto) {
  return useQuery({
    queryKey: inventoryKeys.list(params),
    queryFn: () => inventoriesService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch inventories KPIs
 */
export function useInventoriesKPIs(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: inventoryKeys.kpis(startDate, endDate),
    queryFn: () => inventoriesService.getKPIs(startDate, endDate),
  });
}

/**
 * Hook to fetch single inventory
 */
export function useInventory(id: string | null) {
  return useQuery({
    queryKey: inventoryKeys.detail(id!),
    queryFn: () => inventoriesService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch inventory summary
 */
export function useInventorySummary(id: string | null) {
  return useQuery({
    queryKey: inventoryKeys.summary(id!),
    queryFn: () => inventoriesService.getSummary(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch inventory preview
 */
export function useInventoryPreview(id: string | null) {
  return useQuery({
    queryKey: inventoryKeys.preview(id!),
    queryFn: () => inventoriesService.getPreview(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create an inventory
 */
export function useCreateInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInventoryDto) => inventoriesService.create(data),
    onSuccess: (inventory) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      toast.success(`Inventaire ${inventory.inventoryNumber} cree`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update an inventory
 */
export function useUpdateInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInventoryDto }) =>
      inventoriesService.update(id, data),
    onSuccess: (inventory) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      toast.success(`Inventaire ${inventory.inventoryNumber} mis a jour`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to delete an inventory
 */
export function useDeleteInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => inventoriesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      toast.success('Inventaire supprime');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression');
    },
  });
}

/**
 * Hook to save all lines for a product (bulk + packaging)
 * @see US-21b.05
 */
export function useSaveProductLines() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      inventoryId,
      rawProductId,
      data,
    }: {
      inventoryId: string;
      rawProductId: string;
      data: SaveProductLinesDto;
    }) => inventoriesService.saveProductLines(inventoryId, rawProductId, data),
    onSuccess: async (_result, { inventoryId }) => {
      // Force refetch to get updated lines and summary
      await queryClient.refetchQueries({ queryKey: inventoryKeys.detail(inventoryId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.summary(inventoryId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.preview(inventoryId) });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la sauvegarde');
    },
  });
}

/**
 * Hook to validate an inventory
 */
export function useValidateInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => inventoriesService.validate(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      // Also invalidate stock movements as new ones are created
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      toast.success(`Inventaire ${result.inventory.inventoryNumber} valide`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la validation');
    },
  });
}

/**
 * Hook to cancel an inventory
 */
export function useCancelInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: CancelInventoryDto }) =>
      inventoriesService.cancel(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      // Also invalidate stock movements in case a validated inventory was cancelled
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      toast.success(`Inventaire ${result.inventoryNumber} annule`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? "Erreur lors de l'annulation");
    },
  });
}
